/**
 * User-agent → a human label ("Chrome 126 · macOS").
 *
 * Extracted from `DebugLogViewer` (#184) so the Sessions tab and the Runs tab
 * describe a visitor the SAME way. Two implementations would drift, and the
 * drift would be visible side by side inside ONE modal — a Sessions card saying
 * "Chrome 126 · macOS" while the run row two tabs over said something else
 * about the same person.
 *
 * Deliberately best-effort and lossy. This is a label a reader SCANS, not a
 * fingerprint, and every caller keeps the raw string available (title
 * attribute, free-text search) so a mis-parse costs nothing.
 *
 * A session card is SCANNED, not parsed — a raw 120-char UA string defeats
 * that, so reduce it to "Chrome 126 · macOS". Order matters in BOTH tables:
 * Edge and Opera UAs both contain `Chrome/`, Chrome's contains `Safari/`, and
 * Android's contains `Linux`. Reordering silently mislabels a whole family.
 */
const UA_BROWSERS: Array<[RegExp, string]> = [
  [/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge'],
  [/OPR\/(\d+)/, 'Opera'],
  [/Firefox\/(\d+)/, 'Firefox'],
  [/Chrome\/(\d+)/, 'Chrome'],
  [/Version\/(\d+)[.\d]*\s+(?:Mobile\/\S+\s+)?Safari/, 'Safari'],
]
const UA_PLATFORMS: Array<[RegExp, string]> = [
  [/iPhone|iPad|iPod/, 'iOS'],
  [/Android/, 'Android'],
  [/Mac OS X|Macintosh/, 'macOS'],
  [/Windows/, 'Windows'],
  [/CrOS/, 'ChromeOS'],
  [/Linux/, 'Linux'],
]

/** Best-effort human label for a UA string; null when nothing matches (a bot or
 *  an exotic webview) so the caller can fall back to the raw string. */
export function describeUa(ua: string | undefined): string | null {
  if (!ua) return null
  let browser: string | null = null
  for (const [re, name] of UA_BROWSERS) {
    const m = re.exec(ua)
    if (m) { browser = `${name} ${m[1]}`; break }
  }
  let platform: string | null = null
  for (const [re, name] of UA_PLATFORMS) if (re.test(ua)) { platform = name; break }
  const bits = [browser, platform].filter(Boolean)
  return bits.length ? bits.join(' · ') : null
}
